import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { CustomerFilterParams } from "../../Types/customerDatabaseTypes";
import { useLocation } from "react-router-dom";
import { appRoutes } from "../../constants/appRoutes";
import { storageKeys } from "../../constants/storageKeys";

interface CustomerDatabaseFiltersContextType {
  cxDatabaseFilters: CustomerFilterParams;
  updateCxDatabaseFilters: (params: CustomerFilterParams) => void;
  resetCxDatabaseFilters: () => void;
}

const CustomerDatabaseFiltersContext = createContext<
  CustomerDatabaseFiltersContextType | undefined
>(undefined);

export const useCxDatabaseContext = (): CustomerDatabaseFiltersContextType => {
  const context = useContext(CustomerDatabaseFiltersContext);
  if (!context) {
    throw new Error(
      "useCustomerDatabaseContext must be used within a CustomerDatabaseContextProvider",
    );
  }
  return context;
};

export const CustomerDatabaseFilterContextProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const location = useLocation();
  const [cxDatabaseFilters, setCxDatabaseFilters] =
    useState<CustomerFilterParams>(() => {
      const storedFilters = sessionStorage.getItem(storageKeys.CX_DB_FILTERS);
      if (storedFilters) {
        try {
          return JSON.parse(storedFilters);
        } catch (error) {
          console.error("Error parsing stored filters:", error);
        }
      } else
        return {
          latitude: undefined,
          longitude: undefined,
          address: undefined,
          radius: undefined,
          sortBy: undefined,
          sortOrder: undefined,
        };
    });

  const updateCxDatabaseFilters = useCallback((params) => {
    setCxDatabaseFilters((prevState) => {
      const updatedFilters = params ? { ...prevState, ...params } : prevState;
      return { ...updatedFilters };
    });
  }, []);

  const resetCxDatabaseFilters = () => {
    setCxDatabaseFilters({
      latitude: undefined,
      longitude: undefined,
      radius: undefined,
      sortBy: undefined,
      sortOrder: undefined,
      address: undefined,
    });
    sessionStorage.removeItem(storageKeys.CX_DB_FILTERS);
  };

  useEffect(() => {
    if (
      location &&
      (location?.pathname?.startsWith(appRoutes.CUSTOMER_DATABASE) ||
        location?.pathname?.startsWith(appRoutes.CUSTOMER_PROFILE))
    ) {
      sessionStorage.setItem(
        storageKeys.CX_DB_FILTERS,
        JSON.stringify(cxDatabaseFilters),
      );
    }
  }, [cxDatabaseFilters]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem(
        storageKeys.CX_DB_FILTERS,
        JSON.stringify(cxDatabaseFilters),
      );
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [cxDatabaseFilters]);

  useEffect(() => {
    if (
      location &&
      !location?.pathname?.startsWith(appRoutes.CUSTOMER_DATABASE) &&
      !location?.pathname?.startsWith(appRoutes.CUSTOMER_PROFILE)
    ) {
      resetCxDatabaseFilters();
      sessionStorage.removeItem(storageKeys.CUSTOMER_DB_PAGE);
    }
  }, [location?.pathname]);

  return (
    <CustomerDatabaseFiltersContext.Provider
      value={{
        cxDatabaseFilters,
        updateCxDatabaseFilters,
        resetCxDatabaseFilters,
      }}
    >
      {children}
    </CustomerDatabaseFiltersContext.Provider>
  );
};
